import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Add, Delete, Save, StarBorder } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect } from "react"
import { PerguntaPesquisaSatisfacaoActionsTypes } from "../context/perguntaPesquisaSatisfacaoActions"
import { usePerguntaPesquisaSatisfacaoContext } from "../context/usePerguntaPesquisaSatisfacaoContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    header: {
        marginBottom: theme.spacing(4),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "flex-start",
            gap: theme.spacing(1),
            marginBottom: theme.spacing(3),
        },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    addButton: {
        borderRadius: 20,
        backgroundColor: "#4caf50",
        color: "white",
        fontWeight: 600,
        boxShadow: "0 2px 8px rgba(76,175,80,0.3)",
        "&:hover": {
            backgroundColor: "#45a049",
        },
        [theme.breakpoints.down("sm")]: { fontSize: "0.75rem" },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        padding: theme.spacing(1.5, 4),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(76,175,80,0.3)",
        minWidth: "180px",
        "&:hover": {
            backgroundColor: "#45a049",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(76,175,80,0.4)",
        },
        [theme.breakpoints.down("sm")]: { minWidth: "100%", padding: theme.spacing(2, 3) },
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "center",
        marginTop: theme.spacing(4),
        [theme.breakpoints.down("sm")]: { marginTop: theme.spacing(3) },
    },
    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            "& fieldset": { borderColor: "#e0e0e0" },
            "&:hover fieldset": { borderColor: "#4caf50" },
            "&.Mui-focused fieldset": { borderColor: "#4caf50" },
        },
    },
    ordemCell: { minWidth: 90 },
    textoCell: { minWidth: 220 },
    tipoCell: { minWidth: 140 },
    acaoCell: { minWidth: 60, whiteSpace: "nowrap" as const },
}))

export function TabelaPerguntas() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, salvar, erroCarregamento } = usePerguntaPesquisaSatisfacaoContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const handleAtualizar = (localId: number, campo: string, valor: string | number | null) => {
        dispatch({
            type: PerguntaPesquisaSatisfacaoActionsTypes.ATUALIZAR_CAMPO,
            payload: { localId, campo: campo as never, valor },
        })
    }

    const handleAdicionar = () => {
        dispatch({ type: PerguntaPesquisaSatisfacaoActionsTypes.ADICIONAR })
    }

    const handleRemover = (localId: number, dbId: number | null) => {
        dispatch({
            type: PerguntaPesquisaSatisfacaoActionsTypes.REMOVER,
            payload: { localId, dbId },
        })
    }

    const handleSalvar = async () => {
        for (const p of dados.perguntas) {
            if (!p.descricao.trim()) {
                alertStart("O texto da pergunta não pode estar vazio.", "error")
                return
            }
            if (p.ordem === null || p.ordem === undefined) {
                alertStart("A ordem de todas as perguntas deve ser preenchida.", "error")
                return
            }
        }
        try {
            await salvar()
            alertStart("Perguntas salvas com sucesso!", "success")
        } catch (error) {
            alertStart((error as Error).message || "Erro ao salvar perguntas.", "error")
        }
    }

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <Box className={classes.header}>
                    <Box>
                        <Typography variant="h4" className={classes.titleText}>
                            Perguntas da Pesquisa de Satisfação
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                            Configure as perguntas exibidas no questionário do aplicativo ou site
                        </Typography>
                    </Box>
                </Box>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Box display="flex" alignItems="center">
                            <StarBorder className={classes.iconHeader} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Perguntas
                            </Typography>
                        </Box>
                        <Button
                            className={classes.addButton}
                            startIcon={<Add />}
                            size="small"
                            onClick={handleAdicionar}
                            disabled={!!erroCarregamento}
                        >
                            {isMobile ? "Adicionar" : "Nova Pergunta"}
                        </Button>
                    </Box>
                    <Box className={classes.cardContent}>
                        <TableContainer style={{ overflowX: "auto" }}>
                            <Table size="small" style={{ width: "100%", minWidth: 510 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.ordemCell}>
                                            <Typography
                                                variant="subtitle2"
                                                style={{ fontWeight: 600 }}
                                            >
                                                Ordem
                                            </Typography>
                                        </TableCell>
                                        <TableCell className={classes.textoCell}>
                                            <Typography
                                                variant="subtitle2"
                                                style={{ fontWeight: 600 }}
                                            >
                                                Texto da Pergunta
                                            </Typography>
                                        </TableCell>
                                        <TableCell className={classes.tipoCell}>
                                            <Typography
                                                variant="subtitle2"
                                                style={{ fontWeight: 600 }}
                                            >
                                                Tipo
                                            </Typography>
                                        </TableCell>
                                        <TableCell className={classes.acaoCell} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dados.perguntas.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={4}
                                                align="center"
                                                style={{ padding: "32px 0" }}
                                            >
                                                <Typography style={{ color: "#7f8c8d" }}>
                                                    Nenhuma pergunta cadastrada. Clique em
                                                    &quot;Nova Pergunta&quot; para adicionar.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        dados.perguntas.map(pergunta => (
                                            <TableRow key={pergunta.localId}>
                                                <TableCell className={classes.ordemCell}>
                                                    <TextField
                                                        className={classes.textField}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        type="number"
                                                        value={pergunta.ordem ?? ""}
                                                        onChange={e =>
                                                            handleAtualizar(
                                                                pergunta.localId,
                                                                "ordem",
                                                                e.target.value !== ""
                                                                    ? Number(e.target.value)
                                                                    : null
                                                            )
                                                        }
                                                        disabled={!!erroCarregamento}
                                                        inputProps={{
                                                            min: 1,
                                                            style: { textAlign: "center" },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell className={classes.textoCell}>
                                                    <TextField
                                                        className={classes.textField}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        placeholder="Texto da pergunta"
                                                        value={pergunta.descricao}
                                                        onChange={e =>
                                                            handleAtualizar(
                                                                pergunta.localId,
                                                                "descricao",
                                                                e.target.value
                                                            )
                                                        }
                                                        disabled={!!erroCarregamento}
                                                    />
                                                </TableCell>
                                                <TableCell className={classes.tipoCell}>
                                                    <TextField
                                                        className={classes.textField}
                                                        select
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        value={pergunta.tipo}
                                                        onChange={e =>
                                                            handleAtualizar(
                                                                pergunta.localId,
                                                                "tipo",
                                                                Number(e.target.value)
                                                            )
                                                        }
                                                        disabled={!!erroCarregamento}
                                                    >
                                                        <MenuItem value={1}>Estrelas</MenuItem>
                                                        <MenuItem value={2}>Aberta</MenuItem>
                                                    </TextField>
                                                </TableCell>
                                                <TableCell
                                                    className={classes.acaoCell}
                                                    align="center"
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleRemover(
                                                                pergunta.localId,
                                                                pergunta.id
                                                            )
                                                        }
                                                        disabled={!!erroCarregamento}
                                                        style={{ color: "#e74c3c" }}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Paper>

                <Box className={classes.buttonContainer}>
                    <Button
                        className={classes.saveButton}
                        startIcon={!isMobile && <Save />}
                        onClick={handleSalvar}
                        disabled={!!erroCarregamento}
                        size={isMobile ? "large" : "medium"}
                    >
                        {isMobile ? "Salvar Perguntas" : "Salvar Perguntas"}
                    </Button>
                </Box>
            </div>
        </div>
    )
}
